import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '../../components/ui/alert-dialog';
import DataTable from '../../components/DataTable';
import api from '../../services/api';
import { toast } from 'sonner';
import { formatDateOnly } from '../../utils/timezone';
import {
  useBusinessTimezone,
  getTodayInTz,
  mondayOfWeek,
  addDaysToDateString,
  parseDateOnly,
  formatDateOnlyDisplay,
} from '../../utils/businessTime';
import {
  Wrench,
  Calendar,
  Clock,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  Play,
  XCircle,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  BarChart3,
  RefreshCw,
} from 'lucide-react';

const LANE_COLORS = {
  '1': { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700' },
  '2': { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700' },
  '3': { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700' },
  '4': { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700' },
  '5': { bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-700' },
  '6': { bg: 'bg-cyan-100', border: 'border-cyan-300', text: 'text-cyan-700' },
  '7': { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-700' },
  '8': { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700' },
};
const DEFAULT_LANE_COLOR = { bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-700' };

const MAINTENANCE_TYPES = [
  { value: 'net_replacement', label: 'Net Replacement', icon: '🥅' },
  { value: 'pitch_resurfacing', label: 'Pitch Resurfacing', icon: '🏏' },
  { value: 'equipment_check', label: 'Equipment Check', icon: '🔧' },
  { value: 'deep_cleaning', label: 'Deep Cleaning', icon: '🧹' },
  { value: 'safety_inspection', label: 'Safety Inspection', icon: '🔍' },
  { value: 'general_repair', label: 'General Repair', icon: '🛠️' },
  { value: 'other', label: 'Other', icon: '📋' },
];

const STATUS_CONFIG = {
  scheduled: { label: 'Scheduled', className: 'bg-blue-100 text-blue-700', icon: Calendar },
  in_progress: { label: 'In Progress', className: 'bg-amber-100 text-amber-700', icon: Play },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', className: 'bg-slate-100 text-slate-500', icon: XCircle },
};

const dateOpts = { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' };

const RECURRENCE_PATTERNS = [
  { value: '', label: 'No Recurrence' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const LaneMaintenance = () => {
  // Anchor every date computation on the Business Settings timezone so
  // an admin browsing from a different timezone still sees the same
  // "week" as the academy clock. (See utils/businessTime.js)
  const businessTz = useBusinessTimezone();
  const [LANES, setLANES] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  // selectedWeek = Monday of the currently displayed week, in business TZ.
  const [selectedWeek, setSelectedWeek] = useState(() => mondayOfWeek(getTodayInTz('America/New_York')));
  const [hasNavigatedWeek, setHasNavigatedWeek] = useState(false);
  // Snap selectedWeek to the business-TZ Monday once the live TZ resolves.
  useEffect(() => {
    if (!businessTz || hasNavigatedWeek) return;
    const businessMonday = mondayOfWeek(getTodayInTz(businessTz));
    setSelectedWeek((prev) => (prev === businessMonday ? prev : businessMonday));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTz]);
  const [calendarData, setCalendarData] = useState(null);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    lane_id: '1',
    title: '',
    maintenance_type: 'general_repair',
    start_date: '',
    start_time: '09:00',
    end_date: '',
    end_time: '12:00',
    notes: '',
    estimated_cost: '',
    assigned_to: '',
    is_recurring: false,
    recurrence_pattern: '',
    recurrence_end_date: ''
  });
  
  // Status update form
  const [statusForm, setStatusForm] = useState({
    status: '',
    actual_cost: '',
    completion_notes: '',
    reason: ''
  });

  const fetchSchedules = async () => {
    try {
      const [schedulesRes, summaryRes] = await Promise.all([
        api.get('/lanes/maintenance'),
        api.get('/lanes/maintenance/stats/summary')
      ]);
      setSchedules(schedulesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch maintenance schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarData = async () => {
    try {
      const startDate = selectedWeek;
      const endDate = addDaysToDateString(selectedWeek, 6);
      const response = await api.get(`/lanes/maintenance/calendar?start_date=${startDate}&end_date=${endDate}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar data');
    }
  };

  useEffect(() => {
    fetchSchedules();
    api.get('/lanes').then(res => setLANES(res.data?.map(l => ({ id: l.id, name: l.name })) || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (viewMode === 'calendar') {
      fetchCalendarData();
    }
  }, [viewMode, selectedWeek]);

  const getFilteredSchedules = () => {
    if (activeTab === 'all') return schedules;
    return schedules.filter(s => s.status === activeTab);
  };

  const getStatusCounts = () => {
    const counts = { all: schedules.length, scheduled: 0, in_progress: 0, completed: 0, cancelled: 0 };
    schedules.forEach(s => {
      if (counts[s.status] !== undefined) {
        counts[s.status]++;
      }
    });
    return counts;
  };

  const resetForm = () => {
    const today = getTodayInTz(businessTz);
    setFormData({
      lane_id: '1',
      title: '',
      maintenance_type: 'general_repair',
      start_date: today,
      start_time: '09:00',
      end_date: today,
      end_time: '12:00',
      notes: '',
      estimated_cost: '',
      assigned_to: '',
      is_recurring: false,
      recurrence_pattern: '',
      recurrence_end_date: ''
    });
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null
      };
      const response = await api.post('/lanes/maintenance', payload);
      toast.success('Maintenance schedule created');
      
      if (response.data.conflicting_bookings_count > 0) {
        toast.warning(`Note: ${response.data.conflicting_bookings_count} existing booking(s) may be affected`);
      }
      
      fetchSchedules();
      setShowCreateDialog(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create maintenance schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedSchedule) return;
    
    setActionLoading(true);
    try {
      const payload = {
        ...formData,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null
      };
      await api.put(`/lanes/maintenance/${selectedSchedule.id}`, payload);
      toast.success('Maintenance schedule updated');
      fetchSchedules();
      setShowEditDialog(false);
      setSelectedSchedule(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update maintenance schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedSchedule || !statusForm.status) return;
    
    setActionLoading(true);
    try {
      await api.put(`/lanes/maintenance/${selectedSchedule.id}/status`, statusForm);
      toast.success(`Status updated to ${statusForm.status}`);
      fetchSchedules();
      setShowStatusDialog(false);
      setSelectedSchedule(null);
      setStatusForm({ status: '', actual_cost: '', completion_notes: '', reason: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSchedule) return;
    
    setActionLoading(true);
    try {
      await api.delete(`/lanes/maintenance/${selectedSchedule.id}`);
      toast.success('Maintenance schedule deleted');
      fetchSchedules();
      setShowDeleteDialog(false);
      setSelectedSchedule(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete maintenance schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      lane_id: schedule.lane_id,
      title: schedule.title,
      maintenance_type: schedule.maintenance_type,
      start_date: schedule.start_date,
      start_time: schedule.start_time,
      end_date: schedule.end_date,
      end_time: schedule.end_time,
      notes: schedule.notes || '',
      estimated_cost: schedule.estimated_cost || '',
      assigned_to: schedule.assigned_to || '',
      is_recurring: schedule.is_recurring || false,
      recurrence_pattern: schedule.recurrence_pattern || '',
      recurrence_end_date: schedule.recurrence_end_date || ''
    });
    setShowEditDialog(true);
  };

  const openStatusDialog = (schedule, newStatus) => {
    setSelectedSchedule(schedule);
    setStatusForm({
      status: newStatus,
      actual_cost: schedule.estimated_cost || '',
      completion_notes: '',
      reason: ''
    });
    setShowStatusDialog(true);
  };

  const columns = [
    {
      key: 'lane_id',
      label: 'Lane',
      sortable: true,
      render: (row) => {
        const colors = LANE_COLORS[row.lane_id] || DEFAULT_LANE_COLOR;
        return (
          <Badge className={`${colors.bg} ${colors.text}`}>
            Lane {row.lane_id}
          </Badge>
        );
      }
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <div>
          <p className="font-medium">{row.title}</p>
          <p className="text-xs text-slate-500">
            {MAINTENANCE_TYPES.find(t => t.value === row.maintenance_type)?.icon}{' '}
            {MAINTENANCE_TYPES.find(t => t.value === row.maintenance_type)?.label}
          </p>
          {row.is_recurring && (
            <Badge variant="outline" className="text-xs mt-1">
              <RefreshCw className="w-3 h-3 mr-1" />
              {row.recurrence_pattern}
            </Badge>
          )}
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Request Date',
      sortable: true,
      render: (row) => (
        <div className="text-sm">
          <p className="font-medium">
            {row.created_at
              ? new Date(row.created_at).toLocaleDateString('en-US', { timeZone: businessTz })
              : '—'}
          </p>
          <p className="text-xs text-slate-500">{row.created_by_name}</p>
        </div>
      )
    },
    {
      key: 'start_date',
      label: 'Start Date',
      sortable: true,
      render: (row) => (
        <div className="text-sm">
          <p className="font-medium">{formatDateOnly(row.start_date, dateOpts)}</p>
          <p className="text-slate-500">{row.start_time}</p>
        </div>
      )
    },
    {
      key: 'end_date',
      label: 'End Date',
      sortable: true,
      render: (row) => (
        <div className="text-sm">
          <p className="font-medium">{formatDateOnly(row.end_date, dateOpts)}</p>
          <p className="text-slate-500">{row.end_time}</p>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const config = STATUS_CONFIG[row.status] || STATUS_CONFIG.scheduled;
        const Icon = config.icon;
        return (
          <Badge className={`${config.className} flex items-center gap-1 w-fit`}>
            <Icon className="h-3 w-3" />
            {config.label}
          </Badge>
        );
      }
    },
    {
      key: 'estimated_cost',
      label: 'Cost',
      sortable: true,
      render: (row) => (
        <div className="text-right">
          {row.status === 'completed' && row.actual_cost ? (
            <>
              <p className="font-medium text-green-600">{cs()}{row.actual_cost}</p>
              {row.estimated_cost && row.estimated_cost !== row.actual_cost && (
                <p className="text-xs text-slate-400 line-through">{cs()}{row.estimated_cost} est.</p>
              )}
            </>
          ) : row.estimated_cost ? (
            <p className="text-slate-600">{cs()}{row.estimated_cost} est.</p>
          ) : (
            <span className="text-slate-400">-</span>
          )}
        </div>
      )
    }
  ];

  const actions = (row) => (
    <div className="flex items-center justify-end gap-1">
      {row.status === 'scheduled' && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openStatusDialog(row, 'in_progress')}
            className="text-amber-600 hover:text-amber-700"
            title="Start Maintenance"
            data-testid={`start-${row.id}`}
          >
            <Play className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row)}
            className="text-blue-600 hover:text-blue-700"
            title="Edit"
            data-testid={`edit-${row.id}`}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedSchedule(row);
              setShowDeleteDialog(true);
            }}
            className="text-red-600 hover:text-red-700"
            title="Delete"
            data-testid={`delete-${row.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      )}
      {row.status === 'in_progress' && (
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openStatusDialog(row, 'completed')}
            className="text-green-600 border-green-200 hover:bg-green-50"
            data-testid={`complete-${row.id}`}
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Complete
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openStatusDialog(row, 'cancelled')}
            className="text-slate-600 hover:text-slate-700"
            title="Cancel"
            data-testid={`cancel-${row.id}`}
          >
            <XCircle className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );

  const navigateWeek = (direction) => {
    setHasNavigatedWeek(true);
    setSelectedWeek((prev) => addDaysToDateString(prev, direction * 7));
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(parseDateOnly(addDaysToDateString(selectedWeek, i)));
    }
    return days;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <DashboardLayout title="Lane Maintenance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Lane Maintenance">
      <div className="space-y-6" data-testid="lane-maintenance-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-slate-600">Schedule and track lane maintenance activities</p>
          <Button 
            onClick={() => {
              resetForm();
              setShowCreateDialog(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-maintenance-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Schedule Maintenance
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Scheduled</p>
                    <p className="text-2xl font-bold text-blue-600">{summary.scheduled}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">In Progress</p>
                    <p className="text-2xl font-bold text-amber-600">{summary.in_progress}</p>
                  </div>
                  <Play className="h-8 w-8 text-amber-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Completed (Month)</p>
                    <p className="text-2xl font-bold text-green-600">{summary.completed_this_month}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">This Week</p>
                    <p className="text-2xl font-bold text-purple-600">{summary.upcoming_this_week}</p>
                  </div>
                  <Clock className="h-8 w-8 text-purple-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-slate-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Cost (Month)</p>
                    <p className="text-2xl font-bold text-slate-600">{cs()}{summary.total_cost_this_month || 0}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-slate-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* View Toggle */}
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
            data-testid="view-list"
          >
            <BarChart3 className="h-4 w-4 mr-1" />
            List View
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
            data-testid="view-calendar"
          >
            <Calendar className="h-4 w-4 mr-1" />
            Calendar View
          </Button>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Maintenance Calendar</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => navigateWeek(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-medium">
                    {formatDateOnlyDisplay(selectedWeek, { month: 'short', day: 'numeric', year: undefined })} - 
                    {' '}{formatDateOnlyDisplay(addDaysToDateString(selectedWeek, 6), { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => navigateWeek(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="grid grid-cols-8 gap-1 min-w-[800px]">
                  {/* Header */}
                  <div className="p-2 font-medium text-slate-600">Lane</div>
                  {getWeekDays().map((day, i) => (
                    <div key={i} className="p-2 text-center font-medium text-slate-600 border-b">
                      <p>{day.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                      <p className="text-sm">{day.getDate()}</p>
                    </div>
                  ))}
                  
                  {/* Lane rows */}
                  {LANES.map(lane => (
                    <>
                      <div key={`lane-${lane.id}`} className={`p-2 font-medium ${(LANE_COLORS[lane.id] || DEFAULT_LANE_COLOR).text}`}>
                        {lane.name}
                      </div>
                      {getWeekDays().map((day, i) => {
                        const dateStr = day.toISOString().split('T')[0];
                        const daySchedules = calendarData?.by_lane?.[lane.id]?.filter(s => 
                          s.start_date <= dateStr && s.end_date >= dateStr
                        ) || [];
                        
                        return (
                          <div key={`${lane.id}-${i}`} className="p-1 min-h-[60px] border border-slate-100">
                            {daySchedules.map(s => (
                              <div 
                                key={s.id}
                                className={`text-xs p-1 rounded mb-1 truncate cursor-pointer ${
                                  s.status === 'in_progress' ? 'bg-amber-100 text-amber-800' :
                                  s.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                  'bg-green-100 text-green-800'
                                }`}
                                onClick={() => openEditDialog(s)}
                                title={`${s.title} (${s.start_time}-${s.end_time})`}
                              >
                                {s.title}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <Card className="border border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Wrench className="h-5 w-5 text-slate-600" />
                Maintenance Schedules
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="flex w-full overflow-x-auto lg:w-auto lg:inline-flex mb-4">
                  <TabsTrigger value="all" data-testid="tab-all">
                    All ({statusCounts.all})
                  </TabsTrigger>
                  <TabsTrigger value="scheduled" data-testid="tab-scheduled">
                    <Calendar className="h-4 w-4 mr-1 text-blue-600" />
                    Scheduled ({statusCounts.scheduled})
                  </TabsTrigger>
                  <TabsTrigger value="in_progress" data-testid="tab-in-progress">
                    <Play className="h-4 w-4 mr-1 text-amber-600" />
                    In Progress ({statusCounts.in_progress})
                  </TabsTrigger>
                  <TabsTrigger value="completed" data-testid="tab-completed">
                    <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                    Completed ({statusCounts.completed})
                  </TabsTrigger>
                  <TabsTrigger value="cancelled" data-testid="tab-cancelled">
                    <XCircle className="h-4 w-4 mr-1 text-slate-500" />
                    Cancelled ({statusCounts.cancelled})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value={activeTab}>
                  <DataTable
                    data={getFilteredSchedules()}
                    columns={columns}
                    actions={actions}
                    emptyIcon={Wrench}
                    emptyMessage={activeTab === 'all' ? "No maintenance schedules found." : `No ${activeTab.replace('_', ' ')} maintenance.`}
                    searchable={true}
                    searchFields={['title', 'notes']}
                    testIdPrefix="maintenance"
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setShowEditDialog(false);
            setSelectedSchedule(null);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Wrench className="h-5 w-5 text-blue-600" />
                {showEditDialog ? 'Edit Maintenance Schedule' : 'Schedule Maintenance'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-2 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lane *</Label>
                  <Select
                    value={formData.lane_id}
                    onValueChange={(value) => setFormData({ ...formData, lane_id: value })}
                  >
                    <SelectTrigger data-testid="lane-select">
                      <SelectValue placeholder="Select lane" />
                    </SelectTrigger>
                    <SelectContent>
                      {LANES.map(lane => (
                        <SelectItem key={lane.id} value={lane.id}>{lane.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Maintenance Type *</Label>
                  <Select
                    value={formData.maintenance_type}
                    onValueChange={(value) => setFormData({ ...formData, maintenance_type: value })}
                  >
                    <SelectTrigger data-testid="type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {MAINTENANCE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Weekly net inspection"
                  data-testid="title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    data-testid="start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    data-testid="start-time-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    data-testid="end-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    data-testid="end-time-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estimated Cost</Label>
                  <Input
                    type="number"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                    placeholder="0.00"
                    data-testid="cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <Input
                    value={formData.assigned_to}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    placeholder="Technician name"
                    data-testid="assigned-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional details..."
                  rows={2}
                  data-testid="notes-input"
                />
              </div>
              
              {!showEditDialog && (
                <div className="space-y-4 p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="is_recurring"
                      checked={formData.is_recurring}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                      data-testid="recurring-checkbox"
                    />
                    <Label htmlFor="is_recurring" className="cursor-pointer">
                      <RefreshCw className="w-4 h-4 inline mr-1" />
                      Set as recurring maintenance
                    </Label>
                  </div>
                  
                  {formData.is_recurring && (
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div className="space-y-2">
                        <Label>Recurrence Pattern</Label>
                        <Select
                          value={formData.recurrence_pattern}
                          onValueChange={(value) => setFormData({ ...formData, recurrence_pattern: value })}
                        >
                          <SelectTrigger data-testid="recurrence-select">
                            <SelectValue placeholder="Select pattern" />
                          </SelectTrigger>
                          <SelectContent>
                            {RECURRENCE_PATTERNS.filter(p => p.value).map(pattern => (
                              <SelectItem key={pattern.value} value={pattern.value}>
                                {pattern.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Recurrence End Date</Label>
                        <Input
                          type="date"
                          value={formData.recurrence_end_date}
                          onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                          data-testid="recurrence-end-input"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
              }} disabled={actionLoading}>
                Cancel
              </Button>
              <Button 
                onClick={showEditDialog ? handleUpdate : handleCreate}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={actionLoading}
                data-testid="save-maintenance-btn"
              >
                {actionLoading ? 'Saving...' : (showEditDialog ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Update Dialog */}
        <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                {statusForm.status === 'completed' && <CheckCircle className="h-5 w-5 text-green-600" />}
                {statusForm.status === 'in_progress' && <Play className="h-5 w-5 text-amber-600" />}
                {statusForm.status === 'cancelled' && <XCircle className="h-5 w-5 text-red-600" />}
                {statusForm.status === 'completed' ? 'Complete Maintenance' : 
                 statusForm.status === 'in_progress' ? 'Start Maintenance' : 'Cancel Maintenance'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-2">
              {selectedSchedule && (
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  <p><strong>Title:</strong> {selectedSchedule.title}</p>
                  <p><strong>Lane:</strong> Lane {selectedSchedule.lane_id}</p>
                  <p><strong>Scheduled:</strong> {selectedSchedule.start_date} - {selectedSchedule.end_date}</p>
                </div>
              )}
              
              {statusForm.status === 'completed' && (
                <>
                  <div className="space-y-2">
                    <Label>Actual Cost</Label>
                    <Input
                      type="number"
                      value={statusForm.actual_cost}
                      onChange={(e) => setStatusForm({ ...statusForm, actual_cost: e.target.value })}
                      placeholder="0.00"
                      data-testid="actual-cost-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Completion Notes</Label>
                    <Textarea
                      value={statusForm.completion_notes}
                      onChange={(e) => setStatusForm({ ...statusForm, completion_notes: e.target.value })}
                      placeholder="What was done..."
                      rows={3}
                      data-testid="completion-notes-input"
                    />
                  </div>
                </>
              )}
              
              {statusForm.status === 'cancelled' && (
                <div className="space-y-2">
                  <Label>Cancellation Reason</Label>
                  <Textarea
                    value={statusForm.reason}
                    onChange={(e) => setStatusForm({ ...statusForm, reason: e.target.value })}
                    placeholder="Why is this being cancelled..."
                    rows={3}
                    data-testid="cancel-reason-input"
                  />
                </div>
              )}
            </div>

            <DialogFooter className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowStatusDialog(false)} disabled={actionLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleStatusUpdate}
                className={
                  statusForm.status === 'completed' ? 'bg-green-600 hover:bg-green-700' :
                  statusForm.status === 'in_progress' ? 'bg-amber-600 hover:bg-amber-700' :
                  'bg-red-600 hover:bg-red-700'
                }
                disabled={actionLoading}
                data-testid="confirm-status-btn"
              >
                {actionLoading ? 'Updating...' : 'Confirm'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-xl flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                Delete Maintenance Schedule
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <span>Are you sure you want to delete this maintenance schedule?</span>
                  {selectedSchedule && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg text-sm text-left">
                      <p><strong>Title:</strong> {selectedSchedule.title}</p>
                      <p><strong>Lane:</strong> Lane {selectedSchedule.lane_id}</p>
                      <p><strong>Scheduled:</strong> {selectedSchedule.start_date}</p>
                    </div>
                  )}
                  {selectedSchedule && !selectedSchedule.recurrence_parent_id && selectedSchedule.is_recurring && (
                    <p className="mt-2 text-amber-600 font-medium">
                      ⚠️ This will also delete all recurring schedules created from this one.
                    </p>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                disabled={actionLoading}
                data-testid="confirm-delete-btn"
              >
                {actionLoading ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default LaneMaintenance;
